import React from 'react';
import { Button as ButtonBase } from '../Button';

const Button = ButtonBase as React.ComponentType<
  React.ComponentPropsWithoutRef<'button'>
>;

interface SubmitSectionProps {
  canSubmit: boolean;
  isSubmitClicked: boolean;
  isSubmitting: boolean;
  isFilesUploading: boolean;
  isPPTProcessing: boolean;
  isFilesMissing: boolean;
}

function SpinnerIcon() {
  return (
    <svg
      className="animate-spin h-4 w-4 text-white"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  );
}

function ButtonLabel({
  isSubmitClicked,
  isSubmitting,
  isFilesUploading,
  isPPTProcessing,
  isFilesMissing,
}: Omit<SubmitSectionProps, 'canSubmit'>) {
  if (isSubmitClicked || isSubmitting) {
    return (
      <div className="flex items-center justify-center space-x-2">
        <SpinnerIcon />
        <span>Processing...</span>
      </div>
    );
  }

  if (isFilesUploading) {
    return (
      <div className="flex items-center justify-center space-x-2">
        <SpinnerIcon />
        <span>Uploading Files...</span>
      </div>
    );
  }

  if (isPPTProcessing) {
    return (
      <div className="flex items-center justify-center space-x-2">
        <SpinnerIcon />
        <span>Processing Presentation...</span>
      </div>
    );
  }

  if (isFilesMissing) {
    return <>Upload Required Files</>;
  }

  return <>Submit Application</>;
}

export function SubmitSection({
  canSubmit,
  isSubmitClicked,
  isSubmitting,
  isFilesUploading,
  isPPTProcessing,
  isFilesMissing,
}: SubmitSectionProps) {
  return (
    <div className="border-t border-gray-200 pt-8">
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
        <div className="text-center sm:text-left">
          <p className="text-sm text-gray-600 font-baijamjuree">
            Please review all information and upload required files before
            submitting
          </p>
        </div>
        <Button
          type="submit"
          className={`w-full sm:w-auto bg-black hover:bg-gray-800 text-white rounded-full px-10 py-4 font-baijamjuree-bold shadow-xl transition-all duration-300 disabled:cursor-not-allowed ${
            isSubmitClicked || isSubmitting
              ? 'opacity-60 cursor-wait'
              : 'opacity-100'
          }`}
          disabled={!canSubmit || isSubmitClicked}
        >
          <ButtonLabel
            isSubmitClicked={isSubmitClicked}
            isSubmitting={isSubmitting}
            isFilesUploading={isFilesUploading}
            isPPTProcessing={isPPTProcessing}
            isFilesMissing={isFilesMissing}
          />
        </Button>
      </div>
    </div>
  );
}
